import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { challengesAPI } from "../services/api";
import { motion } from "framer-motion";
import { ChevronRight, Play, CheckCircle2, Flame, Mountain, Compass } from "lucide-react";

/*
 * RECLAIM OUR PASSION — Sacral Chakra / Creativity
 * 
 * "A glowing fire, not a spotlight"
 * Living organic gradients, grain texture, uneven warmth.
 * Darker burnt edges, lighter amber highlights.
 * States of consciousness, not sections of an app.
 */

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "personal_power", label: "Power" },
  { id: "mind_mastery", label: "Mind" },
  { id: "health_energy", label: "Health" },
  { id: "awareness", label: "Awareness" },
  { id: "presence_practices", label: "Presence" },
  { id: "courage", label: "Courage" },
  { id: "discipline", label: "Discipline" },
  { id: "emotional_intelligence", label: "Emotions" },
];

// ─── Challenge Card ──────────────────────────────
const ChallengeCard = ({ challenge, onClick, index }) => {
  const progress = challenge.user_progress;
  const isStarted = progress && progress.status === "in_progress";
  const isCompleted = progress && progress.status === "completed";
  const completedDays = progress?.completed_days?.length || 0;
  const progressPct = isStarted ? Math.round((completedDays / challenge.duration) * 100) : 0;

  const momentumText = isCompleted
    ? "Complete"
    : isStarted
    ? `Day ${completedDays + 1} — ${completedDays === 0 ? 'Beginning' : completedDays < 3 ? 'Building foundation' : completedDays < 5 ? 'Building momentum' : 'Deepening practice'}`
    : `${challenge.duration} days`;

  return (
    <motion.button
      onClick={onClick}
      className="w-full text-left group"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 + index * 0.08, duration: 0.8, ease: 'easeOut' }}
      whileTap={{ scale: 0.98 }}
      data-testid={`challenge-card-${challenge.id}`}
    >
      <div className="relative overflow-hidden rounded-2xl transition-all duration-700"
        style={{
          background: isStarted
            ? 'linear-gradient(145deg, rgba(195,90,30,0.14) 0%, rgba(42,20,8,0.9) 50%, rgba(195,90,30,0.06) 100%)'
            : 'linear-gradient(145deg, rgba(42,20,8,0.8) 0%, rgba(26,12,6,0.9) 60%, rgba(195,90,30,0.04) 100%)',
          border: `1px solid ${isStarted ? 'rgba(224,122,47,0.3)' : 'rgba(195,90,30,0.1)'}`,
          boxShadow: isStarted
            ? '0 4px 30px rgba(195,90,30,0.12), 0 0 60px rgba(195,90,30,0.04) inset'
            : '0 4px 24px rgba(0,0,0,0.3)',
        }}
      >
        {/* Light bloom behind active cards */}
        {isStarted && (
          <motion.div className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(242,166,90,0.08) 0%, transparent 70%)' }}
            animate={{ opacity: [0.4, 0.7, 0.4], scale: [1, 1.05, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}

        <div className="relative z-10 p-5 flex items-center gap-4">
          {/* Icon with ember glow */}
          <div className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center"
            style={{
              background: 'radial-gradient(circle, rgba(195,90,30,0.18) 0%, rgba(195,90,30,0.04) 100%)',
              boxShadow: '0 0 20px rgba(195,90,30,0.06)',
            }}
          >
            {challenge.image_url ? (
              <img src={challenge.image_url} alt="" className="w-full h-full rounded-xl object-cover" style={{ filter: 'brightness(0.75) saturate(0.85)' }} />
            ) : (
              <Flame size={17} style={{ color: '#E07A2F' }} />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium mb-1 truncate"
              style={{ fontFamily: "'Playfair Display', serif", color: '#F5E9DC' }}
            >{challenge.title}</h3>
            <p className="text-[11px] italic truncate"
              style={{ fontFamily: "'Spectral', serif", color: 'rgba(216,191,163,0.45)' }}
            >{momentumText}</p>

            {isStarted && (
              <div className="mt-3 flex items-center gap-2.5">
                <div className="flex-1 h-[3px] rounded-full overflow-hidden" style={{ background: 'rgba(195,90,30,0.1)' }}>
                  <motion.div className="h-full rounded-full"
                    style={{ background: 'linear-gradient(90deg, #C35A1E, #E07A2F, #F2A65A)' }}
                    initial={{ width: 0 }} animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                  />
                </div>
                <span className="text-[10px]" style={{ color: 'rgba(230,181,102,0.55)' }}>{progressPct}%</span>
              </div>
            )}
          </div>

          {/* Status */}
          <div className="flex-shrink-0">
            {isCompleted ? (
              <CheckCircle2 size={16} style={{ color: 'rgba(242,166,90,0.6)' }} />
            ) : isStarted ? (
              <motion.div className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{
                  background: 'radial-gradient(circle, rgba(195,90,30,0.22) 0%, rgba(195,90,30,0.06) 100%)',
                  boxShadow: '0 0 15px rgba(195,90,30,0.1)',
                }}
                animate={{ boxShadow: ['0 0 15px rgba(195,90,30,0.08)', '0 0 20px rgba(195,90,30,0.15)', '0 0 15px rgba(195,90,30,0.08)'] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <Play size={12} style={{ color: '#F2A65A' }} className="ml-0.5" />
              </motion.div>
            ) : (
              <ChevronRight size={15} style={{ color: 'rgba(216,191,163,0.2)' }} />
            )}
          </div>
        </div>
      </div>
    </motion.button>
  );
};

// ─── Main Page ───────────────────────────────────
export const ChallengesPage = () => {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState({ active: [], completed: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [allRes, myRes] = await Promise.all([
        challengesAPI.getAll(),
        challengesAPI.getMyChallenges()
      ]);
      setChallenges(allRes.data);
      setMyChallenges(myRes.data);
      if (myRes.data.active?.length > 0) setActiveChallenge(myRes.data.active[0]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen pb-32 relative overflow-hidden" style={{ background: '#1A0C06' }} data-testid="challenges-page">

      {/* ── Living background layers ── */}

      {/* Base warm gradient — uneven, organic */}
      <div className="fixed inset-0 pointer-events-none" style={{
        background: `
          radial-gradient(ellipse at 35% 15%, rgba(195,90,30,0.1) 0%, transparent 50%),
          radial-gradient(ellipse at 65% 75%, rgba(195,90,30,0.05) 0%, transparent 45%),
          radial-gradient(ellipse at 20% 60%, rgba(169,74,27,0.04) 0%, transparent 40%)
        `,
      }} />

      {/* Breathing fire glow — moves slowly */}
      <motion.div className="fixed pointer-events-none"
        style={{
          width: '130%', height: '40%', left: '-15%', top: '0%',
          background: 'radial-gradient(ellipse at 50% 40%, rgba(195,90,30,0.1) 0%, rgba(224,122,47,0.03) 40%, transparent 65%)',
          filter: 'blur(30px)',
        }}
        animate={{
          opacity: [0.5, 0.8, 0.5],
          x: [-10, 10, -10],
          y: [-5, 5, -5],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Secondary warm shift — creates depth variation */}
      <motion.div className="fixed pointer-events-none"
        style={{
          width: '80%', height: '30%', right: '-10%', bottom: '20%',
          background: 'radial-gradient(ellipse, rgba(242,166,90,0.03) 0%, transparent 60%)',
          filter: 'blur(50px)',
        }}
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
      />

      {/* Grain texture — organic feel */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.045]" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 512 512\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.75\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        backgroundSize: '256px 256px',
      }} />

      {/* Burnt edge vignette */}
      <div className="fixed inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(12,6,2,0.5) 100%)',
      }} />

      {/* ── Content ── */}
      <div className="relative z-10 px-6 pt-16 max-w-lg mx-auto">

        {/* Header */}
        <motion.div className="mb-12"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5 }}
        >
          <motion.p className="text-[10px] uppercase tracking-[0.35em] mb-4"
            style={{ fontFamily: "'Spectral', serif", color: 'rgba(224,122,47,0.6)' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 1.5 }}
          >Reclaim Our Passion</motion.p>

          <h1 className="text-[22px] leading-snug mb-3"
            style={{ fontFamily: "'Playfair Display', serif", color: '#F5E9DC' }}
          >Transform through<br />daily action</h1>

          <p className="text-[13px] italic leading-relaxed"
            style={{ fontFamily: "'Spectral', serif", color: 'rgba(216,191,163,0.4)' }}
          >Choose a challenge and begin today</p>
        </motion.div>

        {/* Continue Today */}
        {activeChallenge && (
          <motion.div className="mb-12"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <button
              onClick={() => navigate(`/challenges/${activeChallenge.challenge?.id || activeChallenge.challenge_id}`)}
              className="w-full py-5 px-6 rounded-2xl text-center transition-all duration-700 relative overflow-hidden"
              style={{
                background: 'linear-gradient(140deg, rgba(195,90,30,0.2) 0%, rgba(42,20,8,0.8) 40%, rgba(224,122,47,0.08) 100%)',
                border: '1px solid rgba(224,122,47,0.3)',
                boxShadow: '0 4px 40px rgba(195,90,30,0.1), 0 0 80px rgba(195,90,30,0.03)',
              }}
              data-testid="challenges-hero-continue-btn"
            >
              {/* Inner bloom */}
              <motion.div className="absolute inset-0 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at 50% 70%, rgba(242,166,90,0.06) 0%, transparent 60%)' }}
                animate={{ opacity: [0.4, 0.8, 0.4] }}
                transition={{ duration: 5, repeat: Infinity }}
              />
              <p className="text-[10px] uppercase tracking-[0.2em] mb-1.5 relative z-10" style={{ color: '#F2A65A' }}>Continue Today</p>
              <p className="text-sm relative z-10" style={{ fontFamily: "'Playfair Display', serif", color: '#F5E9DCdd' }}>
                {activeChallenge.challenge?.title || 'Your Challenge'}
              </p>
              <p className="text-[11px] italic mt-1.5 relative z-10" style={{ fontFamily: "'Spectral', serif", color: 'rgba(216,191,163,0.45)' }}>
                Day {(activeChallenge.completed_days?.length || 0) + 1}
              </p>
            </button>
          </motion.div>
        )}

        {/* Stats */}
        {myChallenges.stats?.total_started > 0 && (
          <motion.div className="flex gap-4 mb-12"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          >
            {[
              { label: 'Completed', value: myChallenges.stats.total_completed || 0 },
              { label: 'In Progress', value: myChallenges.stats.in_progress || 0 },
            ].map(s => (
              <div key={s.label} className="flex-1 py-3.5 px-4 rounded-xl"
                style={{ background: 'rgba(195,90,30,0.05)', border: '1px solid rgba(195,90,30,0.08)' }}
              >
                <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: 'rgba(216,191,163,0.3)' }}>{s.label}</p>
                <p className="text-lg" style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(242,166,90,0.65)' }}>{s.value}</p>
              </div>
            ))}
          </motion.div>
        )}

        {/* Signature Challenge: Hero's Journey */}
        <motion.div className="mb-10"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <button
            onClick={() => navigate('/hero-journey')}
            className="w-full text-left rounded-2xl relative overflow-hidden transition-all"
            style={{
              background: 'linear-gradient(145deg, rgba(120,80,200,0.12) 0%, rgba(26,12,6,0.9) 50%, rgba(120,80,200,0.05) 100%)',
              border: '1px solid rgba(139,92,246,0.2)',
              boxShadow: '0 4px 40px rgba(139,92,246,0.08)',
            }}
            data-testid="hero-journey-card"
          >
            <motion.div className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at 70% 20%, rgba(139,92,246,0.06) 0%, transparent 50%)' }}
              animate={{ opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 7, repeat: Infinity }}
            />
            <div className="relative z-10 p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.15)' }}>
                  <Mountain size={24} style={{ color: 'rgba(196,181,253,0.6)' }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-[9px] uppercase tracking-[0.2em]" style={{ color: 'rgba(196,181,253,0.6)' }}>
                      Signature Challenge
                    </p>
                  </div>
                  <h3 className="text-base mb-0.5" style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(245,240,230,0.85)' }}>
                    The Hero's Journey
                  </h3>
                  <p className="text-[11px] italic" style={{ fontFamily: "'Spectral', serif", color: 'rgba(196,181,253,0.35)' }}>
                    63 days of transformation
                  </p>
                </div>
                <ChevronRight size={18} style={{ color: 'rgba(196,181,253,0.3)' }} />
              </div>
            </div>
          </button>
        </motion.div>

        {/* Category Filter */}
        <motion.div className="mb-8 -mx-2 px-2 overflow-x-auto scrollbar-hide"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
        >
          <div className="flex gap-2 min-w-max pb-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className="px-3.5 py-1.5 rounded-full text-[11px] transition-all whitespace-nowrap"
                style={{
                  background: selectedCategory === cat.id ? 'rgba(195,90,30,0.15)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${selectedCategory === cat.id ? 'rgba(224,122,47,0.3)' : 'rgba(255,255,255,0.05)'}`,
                  color: selectedCategory === cat.id ? 'rgba(242,166,90,0.8)' : 'rgba(216,191,163,0.3)',
                }}
                data-testid={`category-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Challenge List */}
        <div className="space-y-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 rounded-2xl animate-pulse" style={{ background: 'rgba(195,90,30,0.03)' }} />
            ))
          ) : (
            challenges
              .filter(c => selectedCategory === "all" || c.category === selectedCategory)
              .map((challenge, i) => (
              <ChallengeCard key={challenge.id} challenge={challenge} index={i}
                onClick={() => navigate(`/challenges/${challenge.id}`)}
              />
            ))
          )}
          {!loading && challenges.length === 0 && (
            <div className="text-center py-20">
              <Flame className="mx-auto mb-4" size={28} style={{ color: 'rgba(224,122,47,0.3)' }} />
              <p className="text-sm italic" style={{ fontFamily: "'Spectral', serif", color: 'rgba(216,191,163,0.35)' }}>No challenges found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChallengesPage;
